import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { definirMotDePasse, verifierInvitation } from '../services/api';

/**
 * Page publique atteinte via le lien reçu par email : le nouveau membre
 * y choisit son mot de passe, ce qui active son compte.
 */
export default function InvitationPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [visible, setVisible] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const invitationQuery = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => verifierInvitation(token),
    enabled: token !== '',
    retry: false,
  });

  const longueurMin = invitationQuery.data?.longueurMinMotDePasse ?? 8;

  const mutation = useMutation({
    mutationFn: () => definirMotDePasse(token, password, confirmation),
    onSuccess: () => {
      setErreur(null);
      // Laisse le temps de lire la confirmation avant la page de connexion.
      setTimeout(() => navigate('/login'), 2500);
    },
    onError: (error: Error) => setErreur(error.message),
  });

  const validerLocalement = (): string | null => {
    if (password.length < longueurMin) {
      return `Le mot de passe doit contenir au moins ${longueurMin} caractères.`;
    }
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      return 'Le mot de passe doit contenir au moins une lettre et un chiffre.';
    }
    if (password !== confirmation) {
      return 'Les deux mots de passe ne correspondent pas.';
    }
    return null;
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const probleme = validerLocalement();
    if (probleme !== null) {
      setErreur(probleme);
      return;
    }
    setErreur(null);
    mutation.mutate();
  };

  if (invitationQuery.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (invitationQuery.isError) {
    return (
      <div className="p-6 lg:p-10 max-w-md mx-auto pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Lien invalide ou expiré</p>
            <p className="text-sm mt-1">{(invitationQuery.error as Error).message}</p>
            <p className="text-sm mt-2">
              Contactez le bureau du comité des fêtes pour recevoir une nouvelle invitation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (mutation.isSuccess) {
    return (
      <div className="p-6 lg:p-10 max-w-md mx-auto pb-24 lg:pb-0">
        <div className="card bg-green-50 border-green-200 text-green-800 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Votre mot de passe est enregistré</p>
            <p className="text-sm mt-1">Redirection vers la page de connexion…</p>
          </div>
        </div>
      </div>
    );
  }

  const membre = invitationQuery.data?.membre;

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-md mx-auto pb-24 lg:pb-0">
      <div className="text-center space-y-2">
        <ShieldCheck className="w-10 h-10 text-primary-600 mx-auto" />
        <h1 className="text-2xl lg:text-3xl font-bold">Bienvenue {membre?.prenom} !</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {membre?.estBureau
            ? `Votre accès au bureau${membre?.roleBureau ? ` en tant que ${membre.roleBureau}` : ''} est presque prêt.`
            : 'Votre compte bénévole est presque prêt.'}
          {' '}Choisissez votre mot de passe pour l'activer.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Identifiant de connexion : <span className="font-medium text-gray-700 dark:text-gray-300">{membre?.email}</span>
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="relative">
          <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            className="input pl-10 pr-10"
            type={visible ? 'text' : 'password'}
            placeholder="Nouveau mot de passe"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setVisible(!visible)}
            className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
            aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          >
            {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        <div className="relative">
          <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            className="input pl-10"
            type={visible ? 'text' : 'password'}
            placeholder="Confirmez le mot de passe"
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Au moins {longueurMin} caractères, dont une lettre et un chiffre.
        </p>

        {erreur && (
          <p className="text-sm text-red-600 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {erreur}
          </p>
        )}

        <button type="submit" disabled={mutation.isPending} className="btn-primary w-full inline-flex items-center justify-center gap-2">
          {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Activer mon compte
        </button>
      </form>
    </div>
  );
}
